export function createFukushizaiGroupDialogService(deps) {
    // 副資材側の group dialog 対象セクションかを判定します。
    // (登録グループ("group")セクションは廃止済み)
    function isFukushizaiGroupSection(sectionId) {
        return sectionId === "fukushizai";
    }

    // 品質表示側の group dialog 対象セクションかを判定します(副資材とは独立して操作する)。
    function isQualityGroupSection(sectionId) {
        return sectionId === "hinshitsu";
    }

    function isGroupControlSection(sectionId) {
        return isFukushizaiGroupSection(sectionId) || isQualityGroupSection(sectionId);
    }

    // ドメインに応じた既知グループ一覧を返します。
    function knownGroupNosBySection(sectionId, itemCd) {
        return isQualityGroupSection(sectionId)
            ? deps.getKnownQualityGroupNos(itemCd)
            : deps.getKnownFukushizaiGroupNos(itemCd);
    }

    // ドメインに応じた既定のアイテム/グループ選択を返します。
    function selectedItemBySection(sectionId) {
        return isQualityGroupSection(sectionId) ? deps.getSelectedQualityItem() : deps.getSelectedSetItem();
    }
    function selectedGroupBySection(sectionId) {
        return isQualityGroupSection(sectionId) ? deps.getSelectedQualityGroup() : deps.getSelectedGroup();
    }

    // group 追加時に次の group 番号を採番します。
    function getNextFukushizaiGroupNo(itemCd, sectionId = "") {
        const nums = knownGroupNosBySection(sectionId, itemCd)
            .map((value) => Number(deps.normalizeGroupNo(value)))
            .filter((value) => Number.isFinite(value) && value > 0);
        const next = nums.length === 0 ? 1 : Math.max(...nums) + 1;
        return String(next).padStart(2, "0");
    }

    // action ごとに dialog の色候補を組み立てます。
    function getGroupDialogColors(section, action, extra = {}) {
        const normalizedAction = String(action ?? "");
        const sectionId = String(deps.DicVal(section, "id", "") ?? "");
        const quality = isQualityGroupSection(sectionId);
        const targetItem = String(deps.DicVal(extra, "setItem", selectedItemBySection(sectionId)) ?? "").trim();
        const sourceItem = String(deps.DicVal(extra, "fromItem", targetItem) ?? "").trim();
        const targetGroup = deps.normalizeGroupNo(deps.DicVal(extra, "group", selectedGroupBySection(sectionId)));
        const sourceGroup = deps.normalizeGroupNo(deps.DicVal(extra, "fromGroup", targetGroup));

        // 削除時は色選択を使わないため空配列を返します。
        if (normalizedAction === deps.UI_ACTION.GROUP_DELETE) return [];

        // 追加時は新規 group 向けの候補を返します(候補マスタは両ドメイン共通)。
        if (normalizedAction === deps.UI_ACTION.GROUP_ADD) {
            return deps.getFukushizaiColorOptions(targetItem, "", "add");
        }

        // 複写時は複写元 group の色候補を返します(副資材のみ)。
        if (normalizedAction === deps.UI_ACTION.GROUP_COPY) {
            return deps.getFukushizaiColorOptions(sourceItem, sourceGroup, "copy", sourceItem);
        }

        // 編集時はドメインごとの所属カラーを checked にして返します。
        return quality
            ? deps.getQualityColorOptions(targetItem, targetGroup, "edit")
            : deps.getFukushizaiColorOptions(targetItem, targetGroup, "edit");
    }

    // dialog 上でチェック済みの色コード一覧を返します。
    function checkedGroupDialogColorCodes() {
        const dialog = deps.getGroupDialogState();
        return (Array.isArray(dialog.colorOptions) ? dialog.colorOptions : [])
            .filter((item) => !!deps.DicVal(item, "checked", false))
            .map((item) => deps.normalizeKakousijiUiString(deps.DicVal(item, "colorCd", "")))
            .filter((value) => value !== "");
    }

    // group 操作 dialog を開く前に対象データの存在を確認します。
    function openGroupActionDialog(section, action, extra = {}) {
        const normalizedAction = String(action ?? "");
        const sectionId = String(deps.DicVal(section, "id", "") ?? "");
        const quality = isQualityGroupSection(sectionId);
        const normalizedSetItem = String(deps.DicVal(extra, "setItem", selectedItemBySection(sectionId)) ?? "").trim();
        const normalizedGroup = deps.normalizeGroupNo(deps.DicVal(extra, "group", selectedGroupBySection(sectionId)));
        const sourceItem = String(deps.DicVal(extra, "fromItem", normalizedSetItem) ?? "").trim();
        const sourceGroup = deps.normalizeGroupNo(deps.DicVal(extra, "fromGroup", normalizedGroup));
        const effectiveItem = normalizedSetItem || selectedItemBySection(sectionId) || deps.getCopyItem();
        const effectiveGroup = normalizedGroup || deps.normalizeGroupNo(selectedGroupBySection(sectionId)) || deps.normalizeGroupNo(deps.getCopyGroup());
        const hasTargetGroupOption = quality
            ? knownGroupNosBySection(sectionId, effectiveItem).indexOf(effectiveGroup) !== -1
            : (deps.hasFukushizaiUiGroupOption(effectiveItem, effectiveGroup)
                || deps.hasFukushizaiGroupRows(effectiveItem, effectiveGroup));
        const hasSourceGroupOption = deps.hasFukushizaiUiGroupOption(sourceItem || effectiveItem, sourceGroup)
            || deps.hasFukushizaiGroupRows(sourceItem || effectiveItem, sourceGroup);

        // 削除・編集は対象 group が無い場合は dialog を開きません。
        if (isGroupControlSection(sectionId) && normalizedAction === deps.UI_ACTION.GROUP_DELETE && !hasTargetGroupOption) {
            return;
        }
        if (isGroupControlSection(sectionId) && normalizedAction === deps.UI_ACTION.GROUP_EDIT && !hasTargetGroupOption) {
            return;
        }

        // 複写は複写元 group が無い場合は dialog を開きません(副資材のみ)。
        if (isFukushizaiGroupSection(sectionId) && normalizedAction === deps.UI_ACTION.GROUP_COPY && !hasSourceGroupOption) {
            return;
        }

        const nextGroupNo = normalizedAction === deps.UI_ACTION.GROUP_ADD
            ? getNextFukushizaiGroupNo(effectiveItem, sectionId)
            : normalizedGroup;
        const titleMap = {
            [deps.UI_ACTION.GROUP_ADD]: "グループ追加",
            [deps.UI_ACTION.GROUP_DELETE]: quality ? "ケアラベル削除" : "グループ削除",
            [deps.UI_ACTION.GROUP_EDIT]: "グループ編集",
            [deps.UI_ACTION.GROUP_COPY]: "グループ複写"
        };
        const deleteMessage = quality
            ? "表示中のケアラベル(品質表示)を削除します。よろしいですか？"
            : "表示中のグループ(副資材)を削除します。よろしいですか？";

        deps.setGroupDialogState({
            open: true,
            mode: normalizedAction,
            sectionId: sectionId,
            title: titleMap[normalizedAction] || "グループ操作",
            message: normalizedAction === deps.UI_ACTION.GROUP_DELETE ? deleteMessage : "",
            itemCd: effectiveItem,
            groupNo: nextGroupNo,
            sourceItem,
            sourceGroup,
            colorOptions: getGroupDialogColors(section, normalizedAction, extra)
        });
    }

    // dialog state を初期化して閉じます。
    function closeGroupActionDialog() {
        deps.setGroupDialogState({
            open: false,
            mode: "",
            sectionId: "",
            title: "",
            message: "",
            itemCd: "",
            groupNo: "",
            sourceItem: "",
            sourceGroup: "",
            colorOptions: []
        });
    }

    // 色チェック変更を dialog state に反映します。
    function updateGroupDialogColorChecked(colorCd, checked) {
        const target = String(colorCd ?? "").trim();
        deps.setGroupDialogState((prev) => ({
            ...prev,
            colorOptions: (Array.isArray(prev.colorOptions) ? prev.colorOptions : []).map((item) =>
                String(deps.DicVal(item, "colorCd", "") ?? "").trim() === target
                    ? { ...item, checked: !!checked }
                    : item
            )
        }));
    }

    // dialog mode に応じて add/edit/delete をドメイン別に振り分けます。
    function commitGroupActionDialog() {
        const dialog = deps.getGroupDialogState();

        // 対象外 section の場合は安全に閉じます。
        if (!isGroupControlSection(dialog.sectionId)) {
            closeGroupActionDialog();
            return;
        }

        const quality = isQualityGroupSection(dialog.sectionId);

        // group 追加を実行します。
        if (dialog.mode === deps.UI_ACTION.GROUP_ADD) {
            if (quality) {
                deps.applyQualityGroupAddFromDialog();
            } else {
                deps.applyFukushizaiGroupAddFromDialog();
            }
        } else if (dialog.mode === deps.UI_ACTION.GROUP_EDIT) {
            // group 編集を実行します。
            if (quality) {
                deps.applyQualityGroupEditFromDialog();
            } else {
                deps.applyFukushizaiGroupEditFromDialog();
            }
        } else if (dialog.mode === deps.UI_ACTION.GROUP_DELETE) {
            // group 削除を実行します。
            if (quality) {
                deps.applyQualityGroupDelete(dialog.itemCd, dialog.groupNo);
            } else {
                deps.applyFukushizaiGroupDelete(dialog.itemCd, dialog.groupNo);
            }
        }

        closeGroupActionDialog();
    }

    return {
        checkedGroupDialogColorCodes,
        openGroupActionDialog,
        closeGroupActionDialog,
        updateGroupDialogColorChecked,
        commitGroupActionDialog,
        isQualityGroupSection
    };
}
